'use client';

import { Fragment } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { Button } from './common/Button';
import { HeroBackground } from './HeroBackground';

export function Hero() {
  const t = useTranslations('hero');
  const locale = useLocale();

  // The `hero-low-motion` class is set on <html> by the pre-paint inline
  // script in app/layout.tsx — no React state, no post-hydration re-render.
  // Firefox kill-switch is also handled there + via @-moz-document in CSS.
  return (
    <section
      id="home"
      data-header-dark
      className="relative min-h-screen flex flex-col overflow-hidden"
    >
      <HeroBackground />

      {/* Content — vertically centred so tall screens don't leave a big gap on top */}
      <div className="relative z-10 flex-1 flex flex-col justify-center max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pb-24 pt-32">
        <div className="max-w-5xl">
          <p className="font-mono font-normal text-[length:var(--text-eyebrow-hero)] tracking-[var(--hero-label-tracking)] uppercase text-font-primary/70 mb-6">
            {t('eyebrow')}
          </p>
          <h1 className="text-font-primary text-[length:var(--text-headline)] leading-[1.1] tracking-[-0.02em] font-normal mb-10 lg:whitespace-nowrap">
            {t('headline_line1')}
            {t('headline_line2') ? (
              <>
                <br />
                {t('headline_line2')}
              </>
            ) : null}
          </h1>
          <p className="font-thin text-font-primary text-subheadline leading-none mb-8">
            {t('subline')}
          </p>
          <p className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-nav text-font-primary/55 mb-12">
            {(t.raw('industries') as string[]).map((name, i) => (
              <Fragment key={name}>
                {i > 0 && <span aria-hidden className="text-font-primary/30">·</span>}
                <span>{name}</span>
              </Fragment>
            ))}
          </p>
          <div className="flex flex-wrap gap-[25px]">
            <Button variant="light" text={t('cta_consultation')} url={`/${locale}/contact`} />
            <a
              href={`/${locale}/work`}
              className="inline-flex items-center gap-2 font-display text-button tracking-[0.4px] leading-none text-font-primary bg-background-primary/3 px-[24px] py-[22px] rounded-[var(--border-radius-primary)] border-1 border-border-primary/20 hover:bg-white/12 transition-colors"
            >
              {t('cta_show_solutions')}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/icons/arrow-right.svg" alt="" className="size-4 invert" />
            </a>
          </div>
        </div>
      </div>

      {/* Scroll indicator — bottom right (lg+ only to avoid overlap with longer DE/CS subline at tablet widths) */}
      <div className="hidden lg:flex absolute bottom-8 right-8 z-10 items-center gap-2 select-none text-eyebrow text-font-primary bg-background-primary/5 pt-3 pb-3 pl-[18px] pr-[22px] rounded-[var(--border-radius-secondary)] border-1 border-border-primary/15">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src="/icons/mouse.svg" alt="" className="size-5 invert" />
        {t('scroll_label')}
      </div>
    </section>
  );
}
